import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { generatePortalLink } from "@/lib/api/handlers/AttachmentHandling";
import { copyTextFromPromise } from "@/lib/utils/copyTextFromPromise";
import { copyToClipboard } from "@/lib/utils/copyToClipboard";
import { useToast } from "@/components/Toast/Toast";
import {
  acquirePortalLinkMint,
  portalLinkLockKey,
  releasePortalLinkMint,
  usePortalLinkMintLock,
} from "./portalLinkMintLock";

/**
 * The API client has no timeout of its own, and a request the browser keeps
 * open for minutes would leave the lock held and the button dead until the
 * popup is reopened. A late answer after this is dropped (the row it made is a
 * harmless orphan, like a closed tab's).
 */
export const PORTAL_LINK_MINT_TIMEOUT_MS = 30_000;
/** Long enough to select and copy a ~300-character URL by hand. */
export const PORTAL_LINK_MANUAL_TOAST_MS = 20_000;

export type PortalLinkCopyState = "idle" | "minting" | "copied";

/** An ISO instant the browser can read, or `null` (absent on a pre-W3
 *  backend, or garbage) — the caller then says "copied" without a date. */
function readableInstant(value: unknown): string | null {
  return typeof value === "string" && !Number.isNaN(Date.parse(value)) ? value : null;
}

/**
 * "Mint this guest's portal link and put it on the clipboard" — the ONE copy of
 * that flow, used by the popup's header action and by the layout-only portal
 * cell (W3-3). Extracted from Actions.tsx unchanged in what it says:
 *
 *  - "copied" only after the clipboard took the URL;
 *  - the URL handed over in a 20 s toast when the clipboard refused — the link
 *    EXISTS then (a row in "Shared links"), and a "failed" would send the
 *    operator into a retry loop of orphan tokens;
 *  - "could not be created" only when nothing was minted (refusal, network,
 *    the 30 s ceiling, a 404 `guestNotFound` for a guest outside the caller's
 *    rows).
 *
 * The clipboard write STARTS inside the click (`copyTextFromPromise` — WebKit
 * honours a write only while the gesture is active); the text arrives when the
 * mint does. So `copy` must be called synchronously from the click handler.
 *
 * Double-submit: ONE lock per guest across every button of the popup
 * (`portalLinkMintLock`), never a `disabled` button — that would drop keyboard
 * focus onto <body> mid-flow.
 *
 * No attachment scope and no TTL override: an empty `attachmentIds` claim means
 * "every LINK attachment this guest may see" (portal-backend `listVisible`),
 * the ticket block ignores the scope anyway, and the TTL follows the event's
 * `portalTokenTtlDays`. That is also what lets the backend hand the same
 * caller the same still-valid link again instead of minting a new one.
 *
 * @param resetAfterMs how long "copied" stays before the resting label returns;
 *   omitted = it stays (the cell keeps "valid until <date>" readable).
 */
export function usePortalLinkCopy(
  eventId: string | undefined,
  guestId: string | undefined,
  opts: { resetAfterMs?: number } = {},
) {
  const t = useTranslations("guestDetails");
  // "Link copied" is the attachments tab's word — same action, same word.
  const tAttachments = useTranslations("attachments");
  const { showToast } = useToast();
  const [state, setState] = useState<PortalLinkCopyState>("idle");
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The lock THIS component holds, so an unmount mid-mint can give it back. */
  const heldRef = useRef<{ key: string; token: number } | null>(null);
  const lockKey = eventId && guestId ? portalLinkLockKey(eventId, guestId) : null;
  /** Somebody (this button or the other one) is minting for this guest. */
  const locked = usePortalLinkMintLock((s) =>
    lockKey !== null ? s.holders[lockKey] !== undefined : false,
  );
  const { resetAfterMs } = opts;

  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      const held = heldRef.current;
      if (held) releasePortalLinkMint(held.key, held.token);
    },
    [],
  );

  const copy = useCallback(() => {
    if (!eventId || !guestId) return;
    const key = portalLinkLockKey(eventId, guestId);
    const token = acquirePortalLinkMint(key);
    if (token === null) return;
    heldRef.current = { key, token };
    const release = () => {
      releasePortalLinkMint(key, token);
      if (heldRef.current?.token === token) heldRef.current = null;
    };
    setState("minting");
    let ceiling: ReturnType<typeof setTimeout> | undefined;
    let mintedExpiresAt: string | null = null;
    const url = Promise.race([
      generatePortalLink(eventId, guestId),
      new Promise<never>((_, reject) => {
        ceiling = setTimeout(
          () => reject(new Error("mint timeout")),
          PORTAL_LINK_MINT_TIMEOUT_MS,
        );
      }),
    ]).then((result) => {
      if (!result || typeof result !== "object" || "isServerError" in result)
        throw new Error("mint failed");
      if (typeof result.url !== "string" || result.url === "")
        throw new Error("mint failed");
      mintedExpiresAt = readableInstant(result.expiresAt);
      return result.url;
    });
    // Start the clipboard write INSIDE the click (WebKit) — the text arrives
    // when the mint does. Falls back below when the engine has no
    // ClipboardItem or refused.
    const eager = copyTextFromPromise(url);
    void (async () => {
      let copied = false;
      try {
        copied = await eager;
      } catch {
        copied = false;
      }
      let resolved: string;
      try {
        resolved = await url;
      } catch {
        clearTimeout(ceiling);
        // Nothing was minted (server refused / network): the honest word.
        showToast(t("portalLinkCopyFailed"), "error");
        setState("idle");
        release();
        return;
      }
      clearTimeout(ceiling);
      if (!copied) copied = await copyToClipboard(resolved);
      if (copied) {
        setExpiresAt(mintedExpiresAt);
        setState("copied");
        // Announced (the toast is aria-live) as well as shown on the button —
        // below `sm` the header's label is hidden and the icon swap is the
        // only visual.
        showToast(tAttachments("linkCopied"), "success");
        if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
        if (resetAfterMs !== undefined) {
          copiedTimerRef.current = setTimeout(() => setState("idle"), resetAfterMs);
        }
      } else {
        // The link EXISTS (a row in "Shared links") but never reached the
        // clipboard. Hand it over in the toast rather than claim a failure
        // that would send the operator into a retry loop of orphan tokens.
        setState("idle");
        showToast(
          t("portalLinkCopyManual", { url: resolved }),
          "info",
          PORTAL_LINK_MANUAL_TOAST_MS,
        );
      }
      release();
    })();
  }, [eventId, guestId, resetAfterMs, showToast, t, tAttachments]);

  return {
    state,
    /** ISO instant the link stops working — set with "copied", else stale. */
    expiresAt,
    /** A mint for this guest is in flight — from this button or another. */
    busy: state === "minting" || locked,
    copy,
  };
}
